import Decimal from "decimal.js"

import {
  emptyContact,
  emptyContactBoat,
} from "src/components/ContactBoatPicker/emptyShapes"

import { safePercentageDecimal } from "../../../utils/number_helpers"
import { camelCaseToSnakecase } from "../../../utils/string_helpers"
import { centsToDollars, dollarsToCents } from "../../Billing/Items/helpers"
import { parseDateValue } from "../../Billing/helpers"
import { BILLING_CYCLES, PAYMENT_METHODS } from "../constants"

const baseDueOnSignatureInstallment = {
  dueOnSignature: false,
  amountType: "percent",
  amount: 100,
  dueDate: null,
}

const defaultValues = {
  billingCycle: BILLING_CYCLES.CUSTOM_INSTALLMENTS,
  selectedCustomFields: [],
  namedRateId: "custom",
  contractInstallments: [baseDueOnSignatureInstallment],
  installmentDistributionType: "even",
  feesAndDiscounts: [],
  initialDiscountDistributionOption: "evenly",
  rate: {
    pricingStructure: "",
    taxRate: 0,
    longTermStorageProductId: "",
  },
  allowDirectPayment: false,
  acceptedPaymentMethods: [],
  selectedTemplateId: null,
  templateComplete: false,
  withTemplate: false,
  applySurcharge: false,
  lateFeeSettings: [],
  contact: emptyContact,
  contact_id: "",
  contact_boat: emptyContactBoat,
  contact_boat_id: "",
}

const posItemsDataNumericFields = [
  "originalAmount",
  "taxAmount",
  "taxRate",
  "quantity",
  "pricePerUnit",
  "amount",
  "dueDay",
]

const findTemplate = (templates, templateId) => {
  return templates.find(({ id }) => id === templateId)
}

export const getInitialValues = (
  form,
  attachments,
  contact,
  contactBoat,
  isGroup,
  selectedTemplateId,
  withTemplate,
  templates
) => {
  if (!form) {
    return {
      ...defaultValues,
      attachments: attachments || [],
      isGroup,
      withTemplate,
    }
  }

  const initialContact = contact
    ? {
        contact,
        contact_id: contact.id,
        contact_boat: contactBoat,
        contact_boat_id: contactBoat?.id,
      }
    : {}

  const { contractInstallments, installmentDistributionType, ...rate } =
    form.rate
  return {
    ...form,
    rate: {
      ...rate,
      amount: centsToDollars(rate.amount),
      taxRate: safePercentageDecimal(rate.taxRate),
      dueDay: rate.dueDay || 1,
    },
    startDate: parseDateValue(form.startDate),
    endDate: form.endDate ? parseDateValue(form.endDate) : null,
    billingCycle: form.billingCycle,
    customBillingPeriod: !!(form.billingStartDate || form.billingEndDate),
    attachments: attachments || [],
    selectedCustomFields: form.contractSigningEnablements || [],
    namedRateId: form.namedRateId || "custom",
    ...initialContact,
    isGroup,
    contractInstallments:
      contractInstallments && contractInstallments.length
        ? contractInstallments.map(
            ({ amountInCents, dueDate, dueOnSignature, percentAmount }) => {
              const amountType = amountInCents ? "currency" : "percent"
              return {
                dueOnSignature,
                amountType,
                dueDate: dueDate ? parseDateValue(dueDate) : null,
                amount:
                  amountType === "currency"
                    ? centsToDollars(Number(amountInCents))
                    : Number(percentAmount),
              }
            }
          )
        : [baseDueOnSignatureInstallment],
    installmentDistributionType: installmentDistributionType || "custom",
    posItemsData: form.posItemsData
      ? form.posItemsData.map((item) => {
          const keys = Object.keys(item)
          return keys.reduce((formattedItem, key) => {
            const value = item[key]
            return {
              ...formattedItem,
              [key]:
                posItemsDataNumericFields.includes(key) && value
                  ? Number(value)
                  : value,
            }
          }, {})
        })
      : [],
    allowDirectPayment: form.allowDirectPayment ?? false,
    acceptedPaymentMethods: form.acceptedPaymentMethods ?? [],
    feesAndDiscounts: form.feesAndDiscounts ?? [],
    initialDiscountDistributionOption:
      form.initialDiscountDistributionOption ?? "evenly",
    selectedTemplateId,
    withTemplate,
    templateComplete:
      selectedTemplateId && withTemplate
        ? findTemplate(templates, selectedTemplateId)?.complete
        : false,
    applyToQuotes: "false",
    overwriteQuotes: "false",
    applySurcharge: form.applySurcharge ?? false,
  }
}

export const formatContractInstallments = (
  installments,
  isEvenDistribution
) => {
  return installments.map(({ dueOnSignature, dueDate, amountType, amount }) => {
    const isEvenPercent = amountType === "percent" && isEvenDistribution
    const parsedInstallmentAmount = isEvenPercent ? null : Number(amount)
    return {
      amount_type: amountType,
      due_on_signature: dueOnSignature,
      amount: parsedInstallmentAmount,
      due_date: dueOnSignature ? null : dueDate,
    }
  })
}

const formatPosItemsData = (
  items,
  useNewTaxExperience = false,
  isGroup = false
) => {
  return items.map((item) => {
    return Object.fromEntries(
      Object.entries(item)
        .filter(([key]) => {
          // Contract groups don't need tax attributes - taxes come from products when quotes are created
          // But quotes DO need tax attributes
          if (useNewTaxExperience) {
            if (
              (isGroup && key === "itemizedTaxData") ||
              // We also don't need to send combinedTaxRateDisplay or taxRate with new tax experience
              key === "combinedTaxRateDisplay" ||
              key === "taxRate"
            ) {
              return false
            }
          }
          return true
        })
        .map(([key, val]) => {
          let value = val
          if (posItemsDataNumericFields.includes(key) && val) {
            value = Number(val)
          }

          // Convert nested tax attributes to snake_case
          if (key === "itemizedTaxData" && Array.isArray(val)) {
            value = val.map((tax) =>
              Object.fromEntries(
                Object.entries(tax).map(([taxKey, taxVal]) => [
                  camelCaseToSnakecase(taxKey),
                  taxVal,
                ])
              )
            )
          }

          return [camelCaseToSnakecase(key), value]
        })
    )
  })
}

export const formatFeesAndDiscountsData = (feesAndDiscounts) => {
  return feesAndDiscounts.map(
    ({
      id,
      category,
      name,
      percentage,
      amount,
      discountType,
      discountMonth,
      discountStartMonth,
      discountEndMonth,
    }) => {
      return {
        id,
        category,
        name,
        percentage: percentage ? Number(percentage) : null,
        amount: amount ? Number(amount) : null,
        discount_type: discountType,
        discount_month: discountMonth,
        discount_start_month: discountStartMonth,
        discount_end_month: discountEndMonth,
      }
    }
  )
}

// takes what is in lateFeeSettings and transforms it to the necessary
// fields/shape to populate the late fee settings modal form for editing
// purposes
export const parseStoredLateFeeSetting = (lateFee) => {
  if (!lateFee) {
    return
  }

  const { name, percentage, amount, productId, gracePeriodDays } = lateFee

  const baseLateFee = {
    name,
    productId,
    gracePeriodDays: gracePeriodDays.toString(),
  }

  if (percentage) {
    return {
      ...baseLateFee,
      amountType: "percent",
      amount: new Decimal(percentage).mul(100).toString(),
    }
  }
  return {
    ...baseLateFee,
    amountType: "currency",
    amount: new Decimal(amount).div(100).toString(),
  }
}

// takes what is in the late fee settings modal form and transforms it to
// the necessary fields/shape to store within lateFeeSettings
export const formatLateFeeFormData = ({
  amount,
  amountType,
  gracePeriodDays,
  name,
  productId,
}) => {
  const baseLateFee = {
    category: "automated_late_fee",
    name,
    productId,
    gracePeriodDays,
  }

  if (amountType === "percent") {
    return {
      ...baseLateFee,
      percentage: new Decimal(amount).div(100).toString(),
    }
  } else {
    return {
      ...baseLateFee,
      amount: new Decimal(amount).mul(100).toString(),
    }
  }
}

export const formatLateFeeSettingsDataForSubmission = (lateFeeSettings) => {
  return lateFeeSettings.map(
    ({ category, name, amount, percentage, productId, gracePeriodDays }) => {
      return {
        category,
        name,
        amount: amount ? Number(amount) : null,
        percentage: percentage ? Number(percentage) : null,
        product_id: productId,
        grace_period_days: Number(gracePeriodDays),
      }
    }
  )
}

export const setSubmitParams = (
  form,
  useNewTaxExperience = false,
  isGroup = false
) => {
  const isMonthToMonth = form.billingCycle === BILLING_CYCLES.MONTH_TO_MONTH
  const installments = isMonthlyBilling(form)
    ? []
    : formatContractInstallments(
        form.contractInstallments,
        form.installmentDistributionType === "even"
      )
  const feesAndDiscounts = form.feesAndDiscounts ?? []
  const lateFeeSettings = form.lateFeeSettings ?? []
  return {
    id: form.id,
    start_date: form.startDate,
    amount: dollarsToCents(form.rate.amount),
    tax_rate: form.rate.taxRate
      ? new Decimal(form.rate.taxRate).div(100).toNumber()
      : null,
    rate_id: form.rate.id || null,
    pricing_structure: form.rate.pricingStructure,
    billing_cycle: form.billingCycle,
    notes: form.notes,
    attachments: form.attachments ? form.attachments.map(({ id }) => id) : null,
    insurance_requirement: form.insuranceRequirement,
    registration_requirement: form.registrationRequirement,
    pos_items_data: form.posItemsData
      ? JSON.stringify(
          formatPosItemsData(form.posItemsData, useNewTaxExperience, isGroup)
        )
      : null,
    named_rate_id: form.namedRateId === "custom" ? "" : form.namedRateId,
    month_to_month: isMonthToMonth,
    contract_signing_enablements: form.selectedCustomFields
      ? JSON.stringify(
          form.selectedCustomFields.map(
            ({ customFieldDefinitionId, required }) => {
              return {
                custom_field_definition_id: customFieldDefinitionId,
                required,
              }
            }
          )
        )
      : null,
    long_term_storage_product_id: form.rate.longTermStorageProductId,
    allow_direct_payment: form.allowDirectPayment,
    due_day: isMonthlyBilling(form) ? form.rate.dueDay : null,
    end_date: isMonthToMonth ? null : form.endDate,
    contract_installments: JSON.stringify(installments),
    accepted_payment_methods:
      form.acceptedPaymentMethods && form.acceptedPaymentMethods.length
        ? JSON.stringify(form.acceptedPaymentMethods)
        : null,
    fees_and_discounts:
      feesAndDiscounts.length > 0
        ? JSON.stringify(formatFeesAndDiscountsData(feesAndDiscounts))
        : null,
    late_fee_settings:
      lateFeeSettings.length > 0
        ? JSON.stringify(
            formatLateFeeSettingsDataForSubmission(lateFeeSettings)
          )
        : null,
    initial_discount_distribution_option:
      form.billingCycle === BILLING_CYCLES.CUSTOM_INSTALLMENTS &&
      feesAndDiscounts.filter(
        (feeOrDiscount) => feeOrDiscount.category === "discount"
      ).length > 0
        ? form.initialDiscountDistributionOption
        : null,
    docuseal_template_id: form.selectedTemplateId,
    pending: true,
    with_template: form.withTemplate,
    overwrite_quotes: form.overwriteQuotes,
    apply_to_quotes: form.applyToQuotes,
    space_id: form.spaceId,
    apply_convenience_fee:
      form.acceptedPaymentMethods &&
      form.acceptedPaymentMethods.includes(PAYMENT_METHODS.card)
        ? form.applySurcharge
        : false,
  }
}

export const isMonthlyBilling = ({ billingCycle }) =>
  [BILLING_CYCLES.MONTH_TO_MONTH, BILLING_CYCLES.MONTHLY_INSTALLMENTS].includes(
    billingCycle
  )

export const MONTHLY_INSTALLMENTS_ALERT_DISMISSED = {
  INSTALLMENTS_TAB:
    "contracts_form::installments_tab::monthly_installments_alert_dismissed",
  ITINERARY_TAB:
    "contracts_form::itinerary_tab::monthly_installments_alert_dismissed",
}
